import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  Col,
  Row,
  UiFormControl,
  UiFormErrorMessage,
  UiImage,
  UiQuestionnaireLayout,
  UiText,
  UiTextarea,
} from '~/components/elements';
import {ScrollToBottomButton} from '~/components/shared/ScrollToBottomButton';
import {HAIR_LOSS_TELECONSULTATION_KEYS, YES_OPTION} from '~/constants/medical-questionnaires';
import {useInput} from '~/hooks';
import {setNextQuestionnaire, setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {Relation} from '~/redux/questionnaires/types';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {spacing} from '~/utils/theme/spacing';

const initSelectedYesOptions = (arr: Relation[]) => {
  return arr.reduce((res: any, cur: Relation) => {
    if (cur?.key) {
      res[cur?.key] = cur?.value ? cur?.value : '';
      return res;
    }
  }, {});
};

const Questionnaire7Screen = () => {
  const dispatch: AppDispatch = useDispatch();
  const currentAnswer = useSelector(
    (state: RootState) =>
      state.questionnaires?.collectData[HAIR_LOSS_TELECONSULTATION_KEYS.HAIR_LOSS_MEDICATION_OR_TREATMENT]?.data,
  );
  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);

  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(
    i => i.key === HAIR_LOSS_TELECONSULTATION_KEYS.HAIR_LOSS_MEDICATION_OR_TREATMENT,
  );

  const rules = {
    'selectedYesOptions.medicationsTaking': () => {
      if (inputs?.selectedOption === YES_OPTION) {
        return {
          presence: {allowEmpty: false, message: `^This field can't be blank`},
          length: {
            minimum: 5,
            maximum: 300,
            tooShort: '^This field is too short (minimum is %{count} characters)',
            tooLong: '^This field is too long (maximum is %{count} characters)',
            tokenizer: function (value: any) {
              return trimSpaces(value);
            },
          },
        };
      }
      return undefined;
    },
  };

  const {inputs, setInputs, validation, trimSpaces} = useInput(
    {
      selectedOption: currentAnswer?.selectedOption || '',
      relations: currentAnswer?.relations || [],
      selectedYesOptions: currentAnswer?.selectedYesOptions || {},
    },
    {rules},
  );

  const isDisabled =
    !inputs.selectedOption ||
    (inputs.selectedOption === 'YES' &&
      !(
        Object.keys(inputs.selectedYesOptions).length > 1 &&
        !Object.keys(inputs.selectedYesOptions).some(i => trimSpaces(inputs.selectedYesOptions[i]).length < 1)
      ));

  const onClickSubmit = () => {
    if (inputs.selectedOption === YES_OPTION) {
      const errors = validation.getErrors();
      if (errors) {
        return;
      }
    }
    dispatch(
      setQuestionnaireAnswer({
        key: HAIR_LOSS_TELECONSULTATION_KEYS.HAIR_LOSS_MEDICATION_OR_TREATMENT,
        index: questionnaire?.index || '',
        data: {...inputs.selectedYesOptions, ...inputs, selectedOption: inputs.selectedOption},
      }),
    );
    dispatch(setNextQuestionnaire({value: HAIR_LOSS_TELECONSULTATION_KEYS.OTHER_CONDITION_OR_MEDICAL_HISTORY}));
  };

  const onClickOption = (answer: {value: string | number; label: string; relations?: Relation[]}) => {
    const {relations, value} = answer;
    setInputs({selectedOption: value, relations: relations || []});
  };

  const renderOptionRelations = () => {
    if (inputs.selectedOption === YES_OPTION) {
      const selectedOptionRelations = questionnaire?.options?.find(i => i.value === inputs.selectedOption);
      return (
        <>
          {selectedOptionRelations?.relations?.map(relation => {
            if (relation.elementType?.name === 'inputText') {
              return (
                <>
                  <UiText fontSize="md" fontWeight="bold" mt={spacing.medium}>
                    {relation.questions?.default}
                  </UiText>
                  <UiFormControl
                    id="selectedYesOptions.medicationsTaking"
                    isRequired
                    isInvalid={!!validation?.errors?.['selectedYesOptions.medicationsTaking']}
                  >
                    <UiTextarea
                      mt={spacing.small}
                      bg="white"
                      borderRadius={spacing.tiny}
                      placeholder={relation.elementType?.placeholder}
                      value={inputs.selectedYesOptions[relation.key]}
                      onChange={({target}) =>
                        setInputs({
                          selectedYesOptions: {
                            ...inputs.selectedYesOptions,
                            [relation.key]: target.value,
                          },
                        })
                      }
                      minH="200px"
                      maxLength={300}
                    />
                    {validation?.errors?.['selectedYesOptions.medicationsTaking'] && (
                      <UiFormErrorMessage>
                        {validation?.errors?.['selectedYesOptions.medicationsTaking'][0]}
                      </UiFormErrorMessage>
                    )}
                  </UiFormControl>
                </>
              );
            }
            if (relation.elementType?.name === 'selectedOption') {
              return (
                <>
                  <UiText fontSize="md" fontWeight="bold" mt={spacing.medium}>
                    {relation.questions.default}
                  </UiText>
                  <Row w="100%" mt={spacing.small}>
                    {relation.options.map(option => (
                      <ScrollToBottomButton
                        key={`${option.label}-${option.value}`}
                        title={option.label}
                        variant={inputs.selectedYesOptions[relation.key] === option.value ? 'solid' : 'toggle'}
                        onClick={() =>
                          setInputs({
                            selectedYesOptions: {
                              ...inputs.selectedYesOptions,
                              [relation.key]: option.value,
                            },
                          })
                        }
                        height="35px"
                        w="100%"
                        mb={5}
                        mx={2}
                      />
                    ))}
                  </Row>
                </>
              );
            }
            return null;
          })}
        </>
      );
    }
    return null;
  };

  return (
    <UiQuestionnaireLayout
      handleOnNext={onClickSubmit}
      checkoutStep={questionnaire?.checkoutStep}
      actions={questionnaire?.actions}
      disableNext={isDisabled}
    >
      <Col align="center" textAlign="center">
        <UiImage type="icon" name={questionnaire?.icon} boxSize={spacing.mLarge} mt={spacing.small} />
        <UiText fontSize="xl" fontWeight="bold" mt={spacing.medium}>
          {questionnaire?.questions?.default}
        </UiText>
        <Row mt={spacing.small} w="100%">
          {questionnaire?.options?.map(value => (
            <ScrollToBottomButton
              key={`${value.label}-${value.value}`}
              title={value.label}
              variant={inputs.selectedOption === value.value ? 'solid' : 'toggle'}
              onClick={() => onClickOption(value)}
              height="35px"
              w="100%"
              mb={5}
              mx={2}
            />
          ))}
        </Row>
        {renderOptionRelations()}
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default Questionnaire7Screen;
